import React from 'react';
import {
    ArmatuurUnitBasisgegevens,
    ArmatuurUnitData,
} from '../../../../../common/src/domain/customInspectionTypes/amsterdamOverspanningen/inspectieOverspanningen';
import {
    CollapsiblePanel,
    EditPanel,
    EditPanelCell,
    EditPanelCellLabel,
    EditPanelFilterSelect,
    EditPanelInput,
    EditPanelRow,
    PanelBody,
    Tray,
} from '../../../components/elements';
import { ISurveyTemplateUnitData } from '../../../components/Surveys/ISurveyTemplateUnitData';
import { EditPanelTrueFalseUndefined } from '../../../components/utils/editPanel/EditPanelTrueFalseUndefined';
import { BasisgegevensValue } from './components/BasisgegevensValue';
import { EditPanelStraatBagSelect } from './EditPanelStraatBagSelect';
import { EditPanelYesNoEmptyRow } from './EditPanelYesNoEmptyRow';
import { getArmatuurLeverancierstypeOptions } from './getArmatuurLeverancierstypeOptions';
import { SurveyEditPhotosPanel } from './SurveyEditPhotosPanel';
import { useInspectieOverspanningenState } from './useInspectieOverspanningenState';

export function SurveyEditAoInspectieOverspanningenUnitArmatuur(
    props: { surveyId: string; objectId: string; onItemChanged: () => Promise<void> } & ISurveyTemplateUnitData,
) {
    const {
        basisgegevensState,
        formState,
        save: saveDatabase,
        setFromState,
    } = useInspectieOverspanningenState<ArmatuurUnitBasisgegevens, ArmatuurUnitData>(props.surveyId, props.unitId, props.objectId);

    async function save(data: ArmatuurUnitData) {
        await saveDatabase(data);
        await props.onItemChanged();
    }

    async function onChange(name: keyof ArmatuurUnitData, value: any, shouldSave: boolean) {
        const newFormState = { ...formState, [name]: value };

        setFromState(newFormState);
        if (shouldSave) {
            await save(newFormState);
        }
    }
    async function onBlur() {
        if (!formState) {
            throw new Error('formState is not set');
        }
        await save(formState);
    }

    if (formState === undefined) {
        return null;
    }

    return (
        <Tray>
            <EditPanel bottomMargin={true} noPadding={true}>
                <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inventarisatie`, initialIsClosed: false }}>
                    <PanelBody isForm={true}>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Straat (BAG)"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <BasisgegevensValue value={basisgegevensState.straatbag} />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}></EditPanelCell>
                            <EditPanelCell rowCount={8}>
                                <EditPanelStraatBagSelect
                                    onChange={(value) => void onChange('straatBagMutatie', value ?? undefined, true)}
                                    value={formState.straatBagMutatie}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Leverancierstype"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <BasisgegevensValue value={basisgegevensState.leverancierstype} />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCell rowCount={2}></EditPanelCell>
                            <EditPanelCell rowCount={8}>
                                <EditPanelFilterSelect
                                    name="leverancierstypeMutatie"
                                    options={getArmatuurLeverancierstypeOptions()}
                                    value={formState.leverancierstypeMutatie ?? ''}
                                    handleChange={async (value) => onChange('leverancierstypeMutatie', value, true)}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Reeds voorzien van LED?"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelTrueFalseUndefined
                                    size="large"
                                    undefinedText="Leeg"
                                    name="reedsVoorzienVanLed"
                                    value={formState.reedsVoorzienVanLed}
                                    onChange={async (value) => onChange('reedsVoorzienVanLed', value, false)}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={2} text="Opmerking"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelInput
                                    name="opmerkingMutatie"
                                    value={formState.opmerkingMutatie}
                                    handleChange={async (value) => onChange('opmerkingMutatie', value, false)}
                                    handleBlur={onBlur}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </PanelBody>
                </CollapsiblePanel>

                <CollapsiblePanel heading={{ dark: true, text: `${props.unitName} inspectie` }}>
                    <PanelBody isForm={true}>
                        <EditPanelYesNoEmptyRow
                            label="Schade aan armatuur?"
                            name="schadeAanArmatuur"
                            value={formState.schadeAanArmatuur}
                            onChange={async (value) => onChange('schadeAanArmatuur', value, true)}
                        />
                        <EditPanelRow>
                            <EditPanelCellLabel rowCount={4} text="Opmerking"></EditPanelCellLabel>
                            <EditPanelCell rowCount={8}>
                                <EditPanelInput
                                    handleBlur={onBlur}
                                    name="opmerking"
                                    value={formState.opmerking}
                                    handleChange={async (value) => onChange('opmerking', value, false)}
                                />
                            </EditPanelCell>
                        </EditPanelRow>
                    </PanelBody>
                </CollapsiblePanel>

                <SurveyEditPhotosPanel
                    objectId={props.objectId}
                    surveyId={props.surveyId}
                    unitName={props.unitName}
                    photos={formState.fotoBestanden}
                    onPhotosChanged={async (photos) => onChange('fotoBestanden', photos, true)}
                />
            </EditPanel>
        </Tray>
    );
}
